import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '../utils/apiClient';

interface UserProfile {
  id: string;
  name: string;
  email: string;
  description: string;
  role: string;
  profileComplete: boolean;
  createdAt: string;
  updatedAt: string;
}

const UserProfilePage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (userId) {
      loadUserProfile(userId);
    }
  }, [userId]);

  const loadUserProfile = async (id: string) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await apiClient.get(`/v1/users/${id}`);
      const profileData = response.data.data;

      setProfile(profileData);
    } catch (err: any) {
      console.error('Failed to load user profile:', err);
      if (err.response?.status === 404) {
        setError('User not found');
      } else {
        setError('Failed to load user profile');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="container mt-4">
        <div className="row justify-content-center">
          <div className="col-md-8">
            <div className="card">
              <div className="card-body text-center">
                <div className="spinner-border text-primary mb-3" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <h5>Loading Profile...</h5>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-4">
        <div className="row justify-content-center">
          <div className="col-md-8">
            <div className="card border-danger">
              <div className="card-body text-center">
                <i className="fas fa-exclamation-triangle text-danger fa-3x mb-3"></i>
                <h5 className="text-danger">Error Loading Profile</h5>
                <p className="text-muted">{error}</p>
                <div className="mt-3">
                  <button
                    className="btn btn-primary me-2"
                    onClick={() => navigate('/users/search')}
                  >
                    <i className="fas fa-search me-1"></i>
                    Back to Search
                  </button>
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => navigate('/dashboard')}
                  >
                    <i className="fas fa-home me-1"></i>
                    Dashboard
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="row justify-content-center">
        <div className="col-md-8">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h4 className="mb-0">
                <i className="fas fa-user me-2"></i>
                User Profile
              </h4>
              <button
                className="btn btn-outline-secondary"
                onClick={() => navigate(-1)}
              >
                <i className="fas fa-arrow-left me-1"></i>
                Back
              </button>
            </div>
            <div className="card-body">
              {profile && (
                <>
                  <div className="row mb-4">
                    <div className="col-md-8">
                      <h3 className="mb-2">
                        {profile.name}
                        <span className={`badge ms-3 ${profile.role === 'admin' ? 'bg-danger' : 'bg-primary'}`}>
                          {profile.role.toUpperCase()}
                        </span>
                      </h3>
                      <p className="text-muted mb-3">
                        <i className="fas fa-envelope me-2"></i>
                        {profile.email}
                      </p>

                      {profile.profileComplete ? (
                        <span className="badge bg-success">
                          <i className="fas fa-check me-1"></i>
                          Complete Profile
                        </span>
                      ) : (
                        <span className="badge bg-warning">
                          <i className="fas fa-exclamation me-1"></i>
                          Incomplete Profile
                        </span>
                      )}
                    </div>
                    <div className="col-md-4 text-end">
                      <div className="d-grid gap-2">
                        <button className="btn btn-primary">
                          <i className="fas fa-envelope me-1"></i>
                          Send Message
                        </button>
                        <button className="btn btn-outline-primary">
                          <i className="fas fa-user-plus me-1"></i>
                          Connect
                        </button>
                      </div>
                    </div>
                  </div>

                  {profile.description && (
                    <div className="mb-4">
                      <h5 className="fw-bold">About</h5>
                      <div className="card bg-light">
                        <div className="card-body">
                          <p className="mb-0">{profile.description}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="row">
                    <div className="col-md-6">
                      <h5 className="fw-bold">Profile Information</h5>
                      <ul className="list-unstyled">
                        <li className="mb-2">
                          <strong>Role:</strong>{' '}
                          <span className={`badge ${profile.role === 'admin' ? 'bg-danger' : 'bg-primary'}`}>
                            {profile.role.toUpperCase()}
                          </span>
                        </li>
                        <li className="mb-2">
                          <strong>Member Since:</strong> {new Date(profile.createdAt).toLocaleDateString()}
                        </li>
                        <li className="mb-2">
                          <strong>Profile Status:</strong>{' '}
                          <span className={`badge ${profile.profileComplete ? 'bg-success' : 'bg-warning'}`}>
                            {profile.profileComplete ? 'Complete' : 'Incomplete'}
                          </span>
                        </li>
                      </ul>
                    </div>
                    <div className="col-md-6">
                      <h5 className="fw-bold">Activity Summary</h5>
                      <ul className="list-unstyled">
                        <li className="mb-2"><strong>Talks Created:</strong> 0</li>
                        <li className="mb-2"><strong>Sessions Delivered:</strong> 0</li>
                        <li className="mb-2"><strong>Events Organized:</strong> 0</li>
                      </ul>
                      <small className="text-muted">
                        Activity data will be available once the user starts creating content
                      </small>
                    </div>
                  </div>

                  <hr className="my-4" />

                  <div className="row">
                    <div className="col-12">
                      <h5 className="fw-bold mb-3">Public Sessions & Talks</h5>
                      <div className="text-center py-4">
                        <i className="fas fa-microphone fa-3x text-muted mb-3"></i>
                        <h6 className="text-muted">No public sessions available</h6>
                        <p className="text-muted small">
                          This user hasn't created any public talks or sessions yet.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="row mt-4">
                    <div className="col-12">
                      <div className="card bg-light">
                        <div className="card-body">
                          <h6 className="card-title">
                            <i className="fas fa-info-circle me-2"></i>
                            Connect with {profile.name}
                          </h6>
                          <p className="card-text small mb-3">
                            Interested in collaborating or learning more? Send a message to start a conversation.
                          </p>
                          <div className="d-flex gap-2">
                            <button className="btn btn-primary btn-sm">
                              <i className="fas fa-envelope me-1"></i>
                              Send Message
                            </button>
                            <button className="btn btn-outline-primary btn-sm">
                              <i className="fas fa-calendar me-1"></i>
                              Invite to Event
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfilePage;
